/**
 * The project's Data tab (design "Flow Data Browser"): on the left every datastore of the sandbox, grouped by the
 * service that uses it, with its tables / collections / buckets / topics and their counts (on a phone, a select); on
 * the right the picked one — Tables, Documents, Keys, Buckets, Topics, the Graph — or Query. A viewer of the project
 * gets the same, read-only. What was applied can be undone for 10 minutes (the bar at the bottom).
 * Below the stores in use, "Available": every other kind the sandbox supports (utils/dataModel.js SUPPORTED_KINDS),
 * greyed, each with what it is for; picking one offers "Ask the AI to add it", which writes a prompt into the project
 * chat for the person to finish and send (`onAskAI(prompt, { prefill: true })`). A viewer sees them, without the ask.
 * "Restore points" (under Query): the project's data at earlier points, restorable by editors (RestorePoints.jsx); a
 * destructive change that saved one first says so in the bar at the bottom, with a link to them.
 * "In Docker" (overview `docker.stores`): the stores a compose file, an image or a Dockerfile service runs (a compose
 * file's mongo), apart from Flow's own; opened with the same views through `dockerScope` (services/DataService.js),
 * each with its restore points (DockerStore.jsx). One that does not run says why, with Start (an explicit click).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { ArrowCounterClockwise, Archive, Broadcast, CaretDown, CaretRight, ChatCircleText, ClockCounterClockwise, Database, File, Graph, Leaf, MagnifyingGlass, Plus, Stack, X } from '@phosphor-icons/react';
import { EmptyState, ErrorState, Loading, errorText } from './DataCommon';
import TablesView from './TablesView';
import DocumentsView from './DocumentsView';
import BucketsView from './BucketsView';
import RedisView from './RedisView';
import KafkaView from './KafkaView';
import GraphView from './GraphView';
import QueryView from './QueryView';
import RestorePoints from './RestorePoints';
import AddStoreDialog from './AddStoreDialog';
import { DockerPoints, DockerStoppedState, DockerStoreBar } from './DockerStore';
import useProjectServices from '../../hooks/useProjectServices';
import useTabSelectionUrl from '../../hooks/useTabSelectionUrl';
import { dockerScope, getOverview, undoChange } from '../../services/DataService';
import { isRunning } from '../../services/SandboxService';
import { addStorePrompt, dockerPanelStore, dockerSummary, dockerWhere, firstObject, firstStore, formatCount, groupStores, kindLabel, serviceLabel, storeLabel, storeSummary, unusedKinds } from '../../utils/dataModel';
import './Data.css';

const ICONS = { postgres: Database, mysql: Database, sqlite: File, mongodb: Leaf, redis: Stack, neo4j: Graph, s3: Archive, kafka: Broadcast };
const INKS = { postgres: 'is-blue', mysql: 'is-orange', sqlite: 'is-grey', mongodb: 'is-green', redis: 'is-red', neo4j: 'is-purple', s3: 'is-orange', kafka: 'is-grey' };
const QUERY = '::query';
const ADD = '::add::';
const RESTORE = '::restore';
const DOCKER_GROUP = '::docker';
const DOCKER_POLL_MS = 4000;
const DOCKER_POLL_TIMES = 30;

const memoryKey = (projectId) => `flow.data.${projectId}`;
function remembered(projectId) {
  try { return JSON.parse(localStorage.getItem(memoryKey(projectId)) || 'null'); } catch { return null; }
}
function remember(projectId, place) {
  try { localStorage.setItem(memoryKey(projectId), JSON.stringify({ storeId: place.storeId, object: place.object })); } catch { /* not kept */ }
}

function StoreIcon({ kind }) {
  const Icon = ICONS[kind] || Database;
  return <Icon aria-hidden className={`data-store-icon ${INKS[kind] || ''}`} size={18} />;
}

StoreIcon.propTypes = { kind: PropTypes.string };

function UndoBar({ undo, onUndo, onDismiss, onOpenRestore = null }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 15000);
    return () => clearInterval(timer);
  }, []);
  const until = undo.undoUntil ? Date.parse(undo.undoUntil) : NaN;
  const expired = Number.isFinite(until) && until <= now;
  useEffect(() => { if (expired && !undo.state) { onDismiss(); } }, [expired]); // eslint-disable-line react-hooks/exhaustive-deps
  const minutes = Number.isFinite(until) ? Math.max(1, Math.ceil((until - now) / 60000)) : null;
  return (
    <div className="data-undo" data-testid="undo-toast" role="status">
      <span>
        {undo.state === 'undone' ? 'Undone: the data is as it was.' : undo.state === 'failed' ? `Could not undo: ${undo.error}` : undo.summary || 'Changes applied'}
        {!undo.state && minutes ? <span className="data-undo-time"> · Undo for {minutes} more min</span> : null}
        {!undo.state && undo.restorePoint && (
          <span className="data-undo-rp" data-testid="undo-restore-point">
            {' · '}A restore point was saved first{onOpenRestore ? <> (<button className="data-link" onClick={() => onOpenRestore(undo.restorePoint.id)} type="button">Restore points</button>)</> : null}
          </span>
        )}
        {!undo.state && !undo.restorePoint && undo.restorePointWarning && (
          <span className="data-undo-rp" data-testid="undo-restore-point-warning"> · No restore point could be saved first</span>
        )}
      </span>
      {!undo.state && undo.changeId && (
        <button className="data-btn is-on-dark" disabled={undo.busy} onClick={onUndo} type="button">
          <ArrowCounterClockwise aria-hidden size={15} />{undo.busy ? 'Undoing…' : 'Undo'}
        </button>
      )}
      <button aria-label="Dismiss" className="data-btn is-ghost is-on-dark is-icon" onClick={onDismiss} type="button"><X aria-hidden size={14} /></button>
    </div>
  );
}

UndoBar.propTypes = { onDismiss: PropTypes.func.isRequired, onOpenRestore: PropTypes.func, onUndo: PropTypes.func.isRequired, undo: PropTypes.object.isRequired };

function StoppedState({ store, onLeave }) {
  const services = (store.services || []).map(serviceLabel);
  return (
    <EmptyState
      action={onLeave ? <button className="data-btn" onClick={onLeave} type="button">Back to the project</button> : null}
      title={`${storeLabel(store)} is not running`}
    >
      Start a service that uses it{services.length ? ` (${services.join(', ')})` : ''}, then come back: its data shows here.
    </EmptyState>
  );
}

StoppedState.propTypes = { onLeave: PropTypes.func, store: PropTypes.object.isRequired };

function AddStoreState({ info, service, readOnly, viewer, onAskAI, onAdd }) {
  const label = kindLabel(info.kind);
  let action = null;
  if (viewer) {
    action = <p className="data-footnote" data-testid="data-add-viewer">You can view this project only: someone with editor access can add {label}.</p>;
  } else if (!readOnly && (onAdd || onAskAI)) {
    action = (
      <div className="data-add-actions">
        {onAdd && (
          <button className="data-btn is-primary" data-testid="data-add-button" onClick={() => onAdd(info.kind)} type="button">
            <Plus aria-hidden size={15} />Add {label}
          </button>
        )}
        {onAskAI && (
          <button className={`data-btn${onAdd ? '' : ' is-primary'}`} data-testid="data-add-ask" onClick={() => onAskAI(addStorePrompt(info.kind, service), { prefill: true })} type="button">
            <ChatCircleText aria-hidden size={15} />{onAdd ? 'Add with the AI (choose where it is used)' : 'Ask the AI to add it'}
          </button>
        )}
      </div>
    );
  }
  return (
    <EmptyState action={action} title={`${label} is not used yet`}>
      {info.purpose}. {readOnly || !(onAdd || onAskAI)
        ? `When a service of this project uses ${label}, its data shows here.`
        : onAdd
          ? `Add it in one click to the services that use it, or ask the AI to set it up for a purpose you describe.`
          : `The AI adds it to ${service ? `the ${serviceLabel(service)} service` : 'a service'}: the chat opens with the request written, say what it is for and send it.`}
    </EmptyState>
  );
}

AddStoreState.propTypes = { info: PropTypes.object.isRequired, onAdd: PropTypes.func, onAskAI: PropTypes.func, readOnly: PropTypes.bool, service: PropTypes.string, viewer: PropTypes.bool };

export default function DataPanel({ projectId, projectName = null, serviceName = null, onLeave = null, onAskAI = null, focus = null }) {
  const { role, canRun, loaded, services: projectServices, refresh: refreshServices } = useProjectServices(projectId, { intervalMs: 30000 });
  // A viewer (or a role not known yet) gets no write controls.
  const readOnly = role === 'viewer' || !loaded || !canRun;
  const [overview, setOverview] = useState(null);
  const [error, setError] = useState(null);
  const [place, setPlace] = useState(null); // { storeId, object, filter?, mode?: 'query' | 'add' | 'restore', query?, ask?, kind?, pointId? }
  const [availableOpen, setAvailableOpen] = useState(null); // null: open only when nothing is used yet
  const [find, setFind] = useState('');
  // Each datastore's list of tables / collections, opened or closed by its caret: `{ [storeId]: boolean }`. Not set: open
  // for the datastore in view, closed for the others.
  const [storeOpen, setStoreOpen] = useState({});
  const [pendingCount, setPendingCount] = useState(0);
  const [undo, setUndo] = useState(null);
  const [epoch, setEpoch] = useState(0);
  const [adding, setAdding] = useState(null); // the kind whose Add dialog is open

  const loadOverview = useCallback(async () => {
    setError(null);
    try {
      const answer = await getOverview(projectId);
      setOverview(answer);
      return answer;
    } catch (e) {
      // A 404 on the overview itself: the sandbox still runs a vfs-service from before the Data tab (it updates on its
      // next wake, or with the admin's Update).
      setError(e?.status === 404
        ? 'Your sandbox runs an older version without the Data tab. Put it to sleep from the Sandbox page and open the project again (or ask your sandbox admin to Update it).'
        : errorText(e));
      return null;
    }
  }, [projectId]);

  useEffect(() => {
    setOverview(null);
    // Asked for the restore points (the project Home's "Open restore points", a link after a change): straight there.
    const toRestore = focus?.view === 'restore';
    setPlace(toRestore ? { mode: 'restore', pointId: focus.point || null } : null);
    loadOverview().then((answer) => {
      if (toRestore) { return; }
      const stores = [...(answer?.stores || []), ...(answer?.docker?.stores || []).map(dockerPanelStore)];
      // Asked for one store (a link from Traces: "See in Data › Redis"): that one, on the object asked if it has it.
      const focused = focus?.store && stores.find((s) => s.id === focus.store);
      if (focused) {
        setPlace({ storeId: focused.id, object: focused.objects?.some((o) => o.name === focus.object) ? focus.object : firstObject(focused) });
        return;
      }
      const kept = remembered(projectId);
      const keptStore = kept && stores.find((s) => s.id === kept.storeId);
      if (keptStore) {
        setPlace({ storeId: keptStore.id, object: keptStore.objects?.some((o) => o.name === kept.object) ? kept.object : firstObject(keptStore) });
        return;
      }
      // The service open on the page first: its store, else the first one with data.
      const ofService = serviceName && stores.find((s) => s.status === 'running' && (s.services || []).includes(serviceName));
      const start = ofService || firstStore(stores);
      if (start) { setPlace({ storeId: start.id, object: firstObject(start) }); }
    });
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  // The place in the URL (`?tab=data&store=<id>&table=<name>`, `&restore=<point id>|all`), once there is one: a store
  // or table asked for that is gone was replaced above by one that exists. The query and Add pages keep their store only.
  const [pointShown, setPointShown] = useState(undefined);
  useEffect(() => { setPointShown(undefined); }, [place?.mode, place?.pointId]);
  useTabSelectionUrl('data', place?.mode === 'restore'
    ? { store: null, table: null, restore: (pointShown === undefined ? place.pointId : pointShown) || 'all' }
    : { store: place?.mode === 'add' ? null : place?.storeId || null, table: place?.mode ? null : place?.object || null, restore: null }, { ready: Boolean(place || overview) });

  const flowStores = useMemo(() => overview?.stores || [], [overview]);
  const dockerStores = useMemo(() => (overview?.docker?.stores || []).map(dockerPanelStore), [overview]);
  // Flow's stores and the ones in Docker: one list to find a store by id (a Docker store's id is `d-…`, never a kind).
  const stores = useMemo(() => [...flowStores, ...dockerStores], [flowStores, dockerStores]);
  const store = place && (!place.mode || place.mode === 'points') ? stores.find((s) => s.id === place.storeId) : null;
  const groups = useMemo(() => {
    const own = groupStores(flowStores);
    return dockerStores.length ? [...own, { name: DOCKER_GROUP, label: 'In Docker', stores: dockerStores }] : own;
  }, [flowStores, dockerStores]);
  // A kind that runs in Docker is not "Not used yet".
  const available = useMemo(() => (overview ? unusedKinds(stores) : []), [overview, stores]);
  // After Start: the overview again every few seconds until the store runs (a bounded number of times).
  const [polling, setPolling] = useState(0);
  useEffect(() => {
    if (!polling) { return undefined; }
    const timer = setTimeout(() => {
      loadOverview().then((answer) => {
        const running = (answer?.docker?.stores || []).some((d) => d.id === place?.storeId && d.status === 'running');
        setPolling((n) => (running || n >= DOCKER_POLL_TIMES ? 0 : n + 1));
      });
    }, DOCKER_POLL_MS);
    return () => clearTimeout(timer);
  }, [polling]); // eslint-disable-line react-hooks/exhaustive-deps
  // The service a new store goes to: the one open on the page, else the first that uses a store.
  const addService = serviceName || groups.find((g) => g.name && g.name !== DOCKER_GROUP)?.name || null;
  const needle = find.trim().toLowerCase();

  const go = (next) => {
    if (pendingCount > 0 && !window.confirm(`Discard ${pendingCount} change${pendingCount === 1 ? '' : 's'} not applied?`)) { return false; }
    setPendingCount(0);
    setPlace(next);
    if (next && !next.mode) { remember(projectId, next); }
    return true;
  };
  const openStore = (s) => go({ storeId: s.id, object: firstObject(s) });
  const openObject = (s, name, filter = null) => go({ storeId: s.id, object: name, filter });
  const openQuery = (query = null, { ask = false } = {}) => go({ mode: 'query', storeId: place?.storeId || null, back: place, query, ask });
  const openAdd = (kind) => go({ mode: 'add', kind });
  const openRestore = (pointId = null) => go({ mode: 'restore', back: place?.mode === 'restore' ? place.back : place, pointId });
  const openDockerPoints = (s) => go({ mode: 'points', storeId: s.id, back: place });
  const leaveRestore = () => go(place?.back && !place.back.mode ? place.back : (firstStore(stores) ? { storeId: firstStore(stores).id, object: firstObject(firstStore(stores)) } : null));

  const applied = (answer) => {
    loadOverview();
    // A destructive change took a restore point first (`restorePoint: { id, name }`), or could not (`restorePointWarning`).
    if (answer?.changeId || answer?.restorePoint || answer?.restorePointWarning) {
      setUndo({
        changeId: answer.changeId || null, undoUntil: answer.undoUntil || null, summary: answer.summary,
        restorePoint: answer.restorePoint || null, restorePointWarning: answer.restorePointWarning || null,
      });
    }
  };
  const runUndo = async () => {
    setUndo((prev) => ({ ...prev, busy: true }));
    try {
      await undoChange(projectId, undo.changeId);
      setUndo((prev) => ({ ...prev, busy: false, state: 'undone' }));
      setEpoch((n) => n + 1);
      loadOverview();
    } catch (e) {
      setUndo((prev) => ({ ...prev, busy: false, state: 'failed', error: errorText(e) }));
    }
  };

  const objectsMatching = (s) => {
    const objects = s.objects || [];
    return needle ? objects.filter((o) => o.name.toLowerCase().includes(needle)) : objects;
  };
  // Searching opens every datastore with a match; else as its caret was left (the one in view open by default).
  const isStoreOpen = (s) => Boolean(needle) || (storeOpen[s.id] ?? s.id === store?.id);
  const objectsShown = (s) => (isStoreOpen(s) ? objectsMatching(s) : []);
  const toggleStore = (s) => setStoreOpen((current) => ({ ...current, [s.id]: !isStoreOpen(s) }));
  const labelOf = (s) => (s.docker ? s.name : storeLabel(s));
  const summaryOf = (s) => {
    if (s.docker) { return dockerSummary(s); }
    return s.status === 'stopped' ? 'Stopped · start a service that uses it' : storeSummary(s);
  };
  const storeMatches = (s) => !needle || labelOf(s).toLowerCase().includes(needle) || objectsMatching(s).length > 0;
  const availableShown = available.filter((k) => !needle || kindLabel(k.kind).toLowerCase().includes(needle) || k.purpose.toLowerCase().includes(needle));
  // Searching opens the group (a match is not hidden); else as the person left it, open when nothing is used yet.
  const showAvailable = Boolean(needle) || (availableOpen ?? !stores.length);

  const selectValue = place?.mode === 'restore' ? RESTORE : place?.mode === 'query' ? QUERY : place?.mode === 'add' ? `${ADD}${place.kind}` : store ? `${store.id}::${place.object || ''}` : '';
  const onSelect = (value) => {
    if (value === QUERY) { openQuery(); return; }
    if (value === RESTORE) { openRestore(); return; }
    if (value.startsWith(ADD)) { openAdd(value.slice(ADD.length)); return; }
    const [id, object] = value.split('::');
    const s = stores.find((x) => x.id === id);
    if (s) { go({ storeId: s.id, object: object || firstObject(s) }); }
  };

  const objectMeta = store && place?.object ? (store.objects || []).find((o) => o.name === place.object) : null;
  const viewKey = `${place?.storeId}:${place?.object}:${JSON.stringify(place?.filter || null)}:${epoch}`;
  // A store in Docker: the views get its scope as the project and the store under its kind (the routes' `:store`).
  const viewProjectId = store?.docker ? dockerScope(projectId, store.id) : projectId;
  const viewStore = store?.docker ? { ...store, id: store.kind } : store;
  const commonProps = { projectId: viewProjectId, store: viewStore, readOnly, onApplied: applied, onOpenQuery: openQuery };
  const queryStore = place?.mode === 'query' ? stores.find((s) => s.id === place.storeId) : null;
  // "Used in" of a bucket's file: the row (its table filtered on the key) or the document (the collection on its _id).
  const openRef = (ref) => {
    const target = stores.find((s) => s.id === ref.store);
    if (!target) { return; }
    if (ref.store === 'postgres') {
      openObject(target, ref.table, Object.entries(ref.key || {}).map(([column, value]) => ({ column, op: '=', value })));
    } else {
      openObject(target, ref.collection, JSON.stringify({ _id: ref._id }));
    }
  };

  let main = null;
  if (error && !overview) {
    main = <ErrorState error={error} onRetry={loadOverview} />;
  } else if (!overview) {
    main = <Loading label="Finding the project's datastores…" />;
  } else if (place?.mode === 'restore') {
    main = <RestorePoints initialPointId={place.pointId || null} key={`restore:${place.pointId || ''}`} onBack={stores.length ? leaveRestore : null} onSelect={setPointShown} projectId={projectId} projectName={projectName} readOnly={readOnly} />;
  } else if (place?.mode === 'add' && available.some((k) => k.kind === place.kind)) {
    main = <AddStoreState info={available.find((k) => k.kind === place.kind)} onAdd={setAdding} onAskAI={onAskAI} readOnly={readOnly} service={addService} viewer={role === 'viewer'} />;
  } else if (!stores.length) {
    main = (
      <EmptyState title="No datastores yet">
        When a service uses a database, a bucket or a topic (PostgreSQL, SQLite, MongoDB, Redis, Neo4j, S3, Kafka), it shows here with its data. Pick one under Available to {readOnly ? 'see what it is for' : 'add it in one click'}.
      </EmptyState>
    );
  } else if (place?.mode === 'points' && store?.docker) {
    main = <DockerPoints onBack={() => go(place.back && !place.back.mode ? place.back : { storeId: store.id, object: firstObject(store) })} onRestored={() => { setEpoch((n) => n + 1); loadOverview(); }} projectId={projectId} projectName={projectName} readOnly={readOnly} store={store} />;
  } else if (place?.mode === 'query') {
    // From a store in Docker: the query runs on it (its scope), the only store offered.
    const inDocker = Boolean(queryStore?.docker);
    main = (
      <QueryView
        focusAsk={Boolean(place.ask)}
        initialQuery={place.query || null}
        initialStore={inDocker ? queryStore.kind : place.storeId}
        key={`query:${place.storeId}:${place.query}`}
        onApplied={applied}
        onBack={() => go(place.back?.mode !== 'query' && place.back ? place.back : { storeId: firstStore(flowStores)?.id, object: firstObject(firstStore(flowStores)) })}
        projectId={inDocker ? dockerScope(projectId, queryStore.id) : projectId}
        readOnly={readOnly}
        stores={inDocker ? [{ ...queryStore, id: queryStore.kind }] : flowStores}
      />
    );
  } else if (!store) {
    main = <EmptyState title="Pick a datastore">Its tables, documents, files or keys open here.</EmptyState>;
  } else if (store.docker && store.dockerStatus !== 'running') {
    main = <DockerStoppedState onStarted={() => setPolling(1)} projectId={projectId} readOnly={readOnly} store={store} />;
  } else if (store.status === 'stopped') {
    main = <StoppedState onLeave={onLeave} store={store} />;
  } else if (store.kind === 'postgres' || store.kind === 'mysql' || store.kind === 'sqlite') {
    main = place.object
      ? <TablesView {...commonProps} initialFilter={place.filter} stores={stores} key={viewKey} onOpenObject={(name, filter) => openObject(store, name, filter)} onPendingChange={setPendingCount} schema={objectMeta?.schema || null} table={place.object} />
      : <EmptyState title="No tables yet">Tables your service creates ({store.kind === 'mysql' ? 'db/mysql/*.sql' : 'db/init/*.sql'}) show here.</EmptyState>;
  } else if (store.kind === 'mongodb') {
    main = place.object
      ? <DocumentsView {...commonProps} collection={place.object} initialFilter={typeof place.filter === 'string' ? place.filter : null} key={viewKey} />
      : <EmptyState title="No collections yet">Collections your service writes show here.</EmptyState>;
  } else if (store.kind === 'redis') {
    main = <RedisView {...commonProps} key={viewKey} />;
  } else if (store.kind === 's3') {
    main = place.object
      ? <BucketsView {...commonProps} bucket={place.object} key={viewKey} onOpenRef={openRef} />
      : <EmptyState title="No buckets yet">Buckets your service declares (storage/buckets.json) show here.</EmptyState>;
  } else if (store.kind === 'kafka') {
    main = <KafkaView {...commonProps} key={`${store.id}:${place.object}`} onTopicsChanged={loadOverview} topic={place.object || null} />;
  } else if (store.kind === 'neo4j') {
    main = <GraphView key={viewKey} label={place.object} onOpenQuery={openQuery} projectId={viewProjectId} scoped={!store.docker} />;
  } else {
    main = <EmptyState title={`${kindLabel(store.kind)} is not supported here yet`} />;
  }
  // Over the data of a running store in Docker: where it is declared, and its restore points.
  if (store?.docker && store.dockerStatus === 'running' && !place?.mode) {
    main = <div className="data-docker-main"><DockerStoreBar onOpenPoints={() => openDockerPoints(store)} store={store} />{main}</div>;
  }

  return (
    <div className="data-panel" data-testid="data-panel">
      <aside aria-label="Datastores" className="data-rail">
        <label className="data-sr" htmlFor="data-find">Find a table, collection or key</label>
        <input className="data-input is-wide" id="data-find" onChange={(e) => setFind(e.target.value)} placeholder="Find a table, collection, key…" type="search" value={find} />
        <button
          aria-current={place?.mode === 'query' ? 'page' : undefined}
          className={`data-store${place?.mode === 'query' ? ' is-on' : ''}`}
          disabled={!stores.length}
          onClick={() => openQuery()}
          type="button"
        >
          <MagnifyingGlass aria-hidden className="data-store-icon" size={18} />
          <span>Query<small>SQL, Mongo, Cypher, Redis, Kafka</small></span>
        </button>
        <button
          aria-current={place?.mode === 'restore' ? 'page' : undefined}
          className={`data-store${place?.mode === 'restore' ? ' is-on' : ''}`}
          data-testid="data-restore-points"
          onClick={() => openRestore()}
          type="button"
        >
          <ClockCounterClockwise aria-hidden className="data-store-icon" size={18} />
          <span>Restore points<small>Go back to the data of an earlier time</small></span>
        </button>
        {groups.map((group) => {
          const shown = group.stores.filter(storeMatches);
          if (!shown.length) { return null; }
          return (
            <div className="data-rail-group" data-testid={group.name === DOCKER_GROUP ? 'data-docker-group' : undefined} key={group.name || 'none'} role="group" aria-label={group.label}>
              <div className="data-group-label">{group.label}</div>
              {shown.map((s) => {
                const on = s.id === store?.id;
                return (
                  <React.Fragment key={s.id}>
                    <div className="data-store-row">
                      <button aria-current={on && !place?.object ? 'page' : undefined} className={`data-store${on ? ' is-on' : ''}${s.status === 'stopped' ? ' is-stopped' : ''}`} data-testid={`data-store-${s.id}`} onClick={() => openStore(s)} title={s.docker ? dockerWhere(s) : undefined} type="button">
                        <StoreIcon kind={s.kind} />
                        <span>
                          {labelOf(s)}
                          <small>{summaryOf(s)}</small>
                        </span>
                      </button>
                      {(s.objects || []).length > 0 && !needle && (
                        <button
                          aria-controls={`data-objects-${s.id}`}
                          aria-expanded={isStoreOpen(s)}
                          aria-label={`${isStoreOpen(s) ? 'Hide' : 'Show'} the ${labelOf(s)} contents`}
                          className="data-store-caret"
                          data-testid={`data-store-toggle-${s.id}`}
                          onClick={() => toggleStore(s)}
                          type="button"
                        >
                          {isStoreOpen(s) ? <CaretDown aria-hidden size={14} /> : <CaretRight aria-hidden size={14} />}
                        </button>
                      )}
                    </div>
                    {objectsShown(s).length > 0 && (
                      <ul aria-label={`${labelOf(s)} contents`} className="data-objects-list" id={`data-objects-${s.id}`}>
                        {objectsShown(s).map((o) => {
                          const objOn = on && place?.object === o.name;
                          return (
                            <li key={`${o.schema || ''}.${o.name}`}>
                              <button aria-current={objOn ? 'page' : undefined} className={`data-obj${objOn ? ' is-on' : ''}`} data-testid="data-object" onClick={() => openObject(s, o.name)} type="button">
                                <span>{o.name}</span>
                                {o.count !== undefined && o.count !== null && <span className="data-obj-count">{formatCount(o.count)}</span>}
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          );
        })}
        {availableShown.length > 0 && (
          <div aria-label="Available" className="data-rail-group is-available" role="group">
            <button aria-expanded={showAvailable} className="data-group-label data-group-toggle" data-testid="data-available-toggle" onClick={() => setAvailableOpen(!showAvailable)} type="button">
              {showAvailable ? <CaretDown aria-hidden size={11} /> : <CaretRight aria-hidden size={11} />}Available<span className="data-group-count">{availableShown.length}</span>
            </button>
            {showAvailable && availableShown.map((k) => {
              const on = place?.mode === 'add' && place.kind === k.kind;
              return (
                <div className="data-store-row" key={k.kind}>
                  <button aria-current={on ? 'page' : undefined} className={`data-store is-unused${on ? ' is-on' : ''}`} data-testid={`data-available-${k.kind}`} onClick={() => openAdd(k.kind)} type="button">
                    <StoreIcon kind={k.kind} />
                    <span>
                      {kindLabel(k.kind)}
                      <small>{k.purpose} · Not used yet</small>
                    </span>
                  </button>
                  {!readOnly && role !== 'viewer' && (
                    <button aria-label={`Add ${kindLabel(k.kind)}`} className="data-store-add" data-testid={`data-available-add-${k.kind}`} onClick={() => setAdding(k.kind)} title={`Add ${kindLabel(k.kind)}`} type="button">
                      <Plus aria-hidden size={14} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {overview && stores.length > 0 && needle && !groups.some((g) => g.stores.some(storeMatches)) && !availableShown.length && <p className="data-footnote">Nothing matches “{find}”.</p>}
        <p className="data-footnote">Found from the services&apos; settings. Data lives in your sandbox&apos;s data volume; nothing here leaves it.</p>
      </aside>

      <div className="data-rail-select">
        <label className="data-label" htmlFor="data-place">Datastore</label>
        <select className="data-input is-wide" disabled={!overview} id="data-place" onChange={(e) => onSelect(e.target.value)} value={selectValue}>
          {!selectValue && <option value="">Pick a datastore</option>}
          <option value={QUERY}>Query</option>
          <option value={RESTORE}>Restore points</option>
          {groups.map((group) => (
            <optgroup key={group.name || 'none'} label={group.label}>
              {group.stores.flatMap((s) => [
                <option key={`${s.id}::`} value={`${s.id}::${s.objects?.length ? firstObject(s) || '' : ''}`}>
                  {labelOf(s)}{s.docker ? ` (${kindLabel(s.kind)}${s.status === 'stopped' ? ', stopped' : ''})` : s.status === 'stopped' ? ' (stopped)' : ''}
                </option>,
                ...(s.objects || []).map((o) => <option key={`${s.id}::${o.name}`} value={`${s.id}::${o.name}`}>{'  '}{o.name}{o.count !== undefined ? ` (${formatCount(o.count)})` : ''}</option>),
              ]).filter((option, index, all) => all.findIndex((x) => x.props.value === option.props.value) === index)}
            </optgroup>
          ))}
          {available.length > 0 && (
            <optgroup label="Available (not used yet)">
              {available.map((k) => <option key={k.kind} value={`${ADD}${k.kind}`}>{kindLabel(k.kind)} · {k.purpose}</option>)}
            </optgroup>
          )}
        </select>
      </div>

      <main className="data-main">{main}</main>

      {adding && (
        <AddStoreDialog
          kind={adding}
          knownServices={(projectServices || []).map((x) => x.serviceName)}
          onAdded={() => { loadOverview(); refreshServices?.(); }}
          onClose={() => setAdding(null)}
          projectId={projectId}
          runningServices={(projectServices || []).filter(isRunning).map((x) => x.serviceName)}
        />
      )}

      {undo && <UndoBar onDismiss={() => setUndo(null)} onOpenRestore={(pointId) => { setUndo(null); openRestore(pointId); }} onUndo={runUndo} undo={undo} />}
    </div>
  );
}

DataPanel.propTypes = {
  // `{ store, object? }`: the store to open first (its id is its kind), instead of the one last used; `{ view: 'restore',
  // point? }`: the restore points (one of them picked).
  focus: PropTypes.shape({ object: PropTypes.string, point: PropTypes.string, store: PropTypes.string, view: PropTypes.string }),
  // (prompt, { prefill: true }) => void: writes the prompt into the project chat (App's askAIInProjectChat).
  onAskAI: PropTypes.func,
  onLeave: PropTypes.func,
  projectId: PropTypes.string.isRequired,
  // The name the restore confirmation asks for (else read from the project's .mia/project.json).
  projectName: PropTypes.string,
  serviceName: PropTypes.string,
};
